/* The category taxonomy editor: list, create, inline edit, delete-with-reassign.

   Owns the API calls for this section; the row below it is presentational. Only
   one row edits at a time, and the create form is a row-shaped editor rather than
   a separate surface. */

import { LitElement, html, css } from 'lit';
import { shadowReset } from '../../design/base.js';
import { isCoarsePointer } from '../../core/platform.js';
import { settingsRow } from './row.js';
import { customElement, property, state, query } from 'lit/decorators.js';
import type { Category } from '../../core/types.js';
import {
  createCategory,
  deleteCategory,
  updateCategory,
  NOT_SIGNED_IN,
} from '../../core/api.js';
import '../../component/g-button/g-button.js';
import '../../component/g-dialog/g-dialog.js';
import '../../component/g-field/g-field.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';
import '../../component/g-select/g-select.js';
import './settings-category-row.js';
import '../../component/g-action-sheet/g-action-sheet.js';
import type { GDialog } from '../../component/g-dialog/g-dialog.js';
import type { SheetAction } from '../../component/g-action-sheet/g-action-sheet.js';

/** What the action sheet offers, and the ids `act` reports back. */
const ACTIONS: SheetAction[] = [
  { id: 'edit', label: 'Edit', icon: 'edit', note: 'Change the name or the description.' },
  {
    id: 'delete',
    label: 'Delete',
    icon: 'trash',
    note: 'Asks where its items should go, then removes the category.',
    tone: 'danger',
  },
];

@customElement('settings-categories')
export class SettingsCategories extends LitElement {
  static override styles = [
    shadowReset,
    settingsRow,
    css`
      :host {
        display: block;
      }
      /* The form opens in the Add row's place, so it inherits that row's rule —
         .sep from row.ts, repeated because this is a block, not a row. */
      .add {
        position: relative;
      }
      .add::before {
        content: '';
        position: absolute;
        top: 0;
        left: var(--row-pad-l);
        right: var(--row-pad-r);
        height: 1px;
        background: var(--border);
      }
      /* The one accent control on the page. The row's inherit chain carries it to
         the plus glyph too, which draws with currentColor.

         The selector is button.newRow, not .newRow. settingsRow styles button.row
         with color: inherit and font: inherit, which at (0,1,1) outranks a bare
         class and silently painted this row as ordinary body text for as long as
         the rule has existed. No backticks in here, ever: this is a css template
         literal and one would close it. Found by the native parity sweep in
         round 4, where the native Add row renders what this rule says and the
         web did not. */
      button.newRow {
        color: var(--accent);
        font-weight: 700;
        --icon-size: 16px;
      }
      .add {
        display: grid;
        gap: var(--space-2);
        padding: var(--space-3) var(--row-pad-r) var(--space-3)
          var(--row-pad-l);
      }
      .addActs {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-2);
      }
      .moving {
        margin: 0 0 var(--space-4);
        font-size: 0.9rem;
      }
    `,
  ];

  @property({ attribute: false }) categories: Category[] = [];
  @state() private adding = false;
  @state() private editingId: number | null = null;
  @state() private pendingDelete: Category | null = null;
  @state() private reassignTo = '';
  @state() private error = '';
  @state() private newName = '';
  @state() private newDesc = '';
  /** The row whose action sheet is up; touch only, since only that arm has a ⋮. */
  @state() private menuFor: Category | null = null;
  /** Read once, per core/platform: a phone does not become a desktop. */
  private readonly touch = isCoarsePointer();

  @query('g-dialog') private dialog?: GDialog;

  /**
   * Server order, but the protected catch-all ('Other') pinned last.
   *
   * It isn't a peer of the real categories — it's the fallback everything else
   * drains into — so it reads wrong sitting mid-list. Sorted here rather than
   * server-side because it's a presentation concern; /api/categories stays a
   * plain ordered list. Note `sort` mutates, hence the copy.
   */
  private get ordered(): Category[] {
    return [...this.categories].sort((a, b) => Number(a.protected) - Number(b.protected));
  }

  /** Tell the parent to re-read /api/categories (and /api/meta counts with it). */
  private changed(): void {
    this.dispatchEvent(new CustomEvent('changed', { bubbles: true, composed: true }));
  }

  private fail(e: unknown): void {
    this.error = e instanceof Error ? e.message : String(e);
  }

  private async save(c: Category, patch: { name: string; description: string }): Promise<void> {
    this.error = '';
    try {
      const r = await updateCategory(c.id, patch);
      if (r === NOT_SIGNED_IN) return;
      this.editingId = null;
      this.changed();
    } catch (e) {
      this.fail(e);
    }
  }

  private async create(): Promise<void> {
    const name = this.newName.trim();
    if (!name) return;
    this.error = '';
    try {
      const r = await createCategory(name, this.newDesc.trim());
      if (r === NOT_SIGNED_IN) return;
      this.adding = false;
      this.newName = '';
      this.newDesc = '';
      this.changed();
    } catch (e) {
      this.fail(e);
    }
  }

  private askDelete(c: Category): void {
    this.pendingDelete = c;
    // Default target is Other, matching the server's own default.
    this.reassignTo = String(this.categories.find((x) => x.protected)?.id ?? '');
    this.updateComplete.then(() => this.dialog?.show());
  }

  private async confirmDelete(): Promise<void> {
    const c = this.pendingDelete;
    if (!c) return;
    this.error = '';
    try {
      const to = this.reassignTo ? Number(this.reassignTo) : undefined;
      const r = await deleteCategory(c.id, to);
      if (r === NOT_SIGNED_IN) return;
      this.dialog?.close();
      this.pendingDelete = null;
      this.changed();
    } catch (e) {
      this.dialog?.close();
      this.fail(e);
    }
  }

  /** Row-shaped rather than a separate surface: adding a category is the same
      shape of edit as renaming one, so it opens where the new row will land. */
  private renderAdd() {
    return html`
      <div class="add">
        <g-field
          shape="box"
          label="Name"
          .value=${this.newName}
          .debounce=${0}
          @g-input=${(e: CustomEvent<string>) => (this.newName = e.detail)}
        ></g-field>
        <!-- Single-line: see the note in settings-category-row.ts — the value lands
             on one line of the analysis prompt, so a textarea invited input that
             could forge a prompt rule. -->
        <g-field
          shape="box"
          label="Description"
          placeholder="what belongs in this category…"
          .value=${this.newDesc}
          .debounce=${0}
          @g-input=${(e: CustomEvent<string>) => (this.newDesc = e.detail)}
        ></g-field>
        <div class="addActs">
          <g-button @click=${() => (this.adding = false)}>Cancel</g-button>
          <g-button variant="primary" @click=${this.create}>Add</g-button>
        </div>
      </div>
    `;
  }

  /**
   * Edit runs straight from the sheet; delete needs to ask where the items go, so
   * it escalates to the dialog. The sheet has already given up its history entry
   * by then, so the dialog is not opening against a pop in flight.
   */
  private renderMenu() {
    const c = this.menuFor;
    if (!c) return '';
    return html`
      <g-action-sheet
        heading=${c.name}
        meta=${`${c.n} item${c.n === 1 ? '' : 's'}`}
        .actions=${ACTIONS}
        @act=${(e: CustomEvent<string>) => {
          if (e.detail === 'edit') {
            this.editingId = c.id;
            this.error = '';
          } else {
            this.askDelete(c);
          }
        }}
        @dismiss=${() => (this.menuFor = null)}
      ></g-action-sheet>
    `;
  }

  private renderDeleteDialog() {
    const c = this.pendingDelete;
    if (!c) return '';
    const targets = this.ordered
      .filter((x) => x.id !== c.id)
      .map((x) => ({ value: String(x.id), label: x.name }));
    return html`
      <g-dialog heading=${`Delete “${c.name}”?`} @g-close=${() => (this.pendingDelete = null)}>
        <p class="moving">
          ${c.n === 0
            ? 'Nothing is filed here, so nothing moves.'
            : html`${c.n} saved item${c.n === 1 ? '' : 's'} will move to:`}
        </p>
        ${c.n > 0
          ? html`<g-select
              label="Move to"
              .options=${targets}
              .value=${this.reassignTo}
              @g-change=${(e: CustomEvent<string>) => (this.reassignTo = e.detail)}
            ></g-select>`
          : ''}
        <g-button slot="actions" @click=${() => this.dialog?.close()}>Cancel</g-button>
        <g-button slot="actions" variant="primary" @click=${this.confirmDelete}>Delete</g-button>
      </g-dialog>
    `;
  }

  override render() {
    return html`
      ${this.ordered.map(
        (c) => html`
          <settings-category-row
            .category=${c}
            .editing=${this.editingId === c.id}
            .touch=${this.touch}
            @edit=${() => {
              this.editingId = c.id;
              this.error = '';
            }}
            @menu=${() => (this.menuFor = c)}
            @cancel=${() => (this.editingId = null)}
            @save=${(e: CustomEvent<{ name: string; description: string }>) =>
              this.save(c, e.detail)}
            @remove=${() => this.askDelete(c)}
          ></settings-category-row>
        `,
      )}

      ${this.adding
        ? this.renderAdd()
        : html`<button
            class="row sep newRow"
            @click=${() => {
              this.adding = true;
              this.error = '';
            }}
          >
            <g-icon name="plus"></g-icon>Add new category
          </button>`}

      ${this.error ? html`<g-notice class="text" tone="danger">${this.error}</g-notice>` : ''}
      ${this.renderMenu()} ${this.renderDeleteDialog()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'settings-categories': SettingsCategories;
  }
}
